import base64 from "base-64";

/**
 * Armazenamento de tokens (MVP)
 *
 * Objetivo:
 * - Centralizar como o `src-next` lê/escreve tokens.
 * - Diferenciar claramente token de USUÁRIO e token de APLICAÇÃO.
 *
 * Importante:
 * - Por enquanto, usamos `sessionStorage` por simplicidade.
 * - Ainda NÃO implementamos refresh token automático no http client.
 * - Este módulo já deixa o "gancho" para evoluir depois sem quebrar importações.
 */

const USER_ACCESS_TOKEN_KEY = "@fluxmed:user_access_token";
const USER_ACCESS_TOKEN_EXPIRES_AT_KEY =
  "@fluxmed:user_access_token_expires_at";
const APP_ACCESS_TOKEN_KEY = "@fluxmed:app_access_token";
const REFRESH_TOKEN_KEY = "@fluxmed:refresh_token";
const CONSUMER_KEY =
  import.meta.env.VITE_CONSUMER_KEY || "czydwfiWxE8GDSNBtk8JCLYDUqoa";
const CONSUMER_SECRET =
  import.meta.env.VITE_CONSUMER_SECRET || "REW8WZ2boh32M1wtYOL6o84mM2Aa";

// ---------------------------------------------------------------------------
// Tokens de USUÁRIO
// ---------------------------------------------------------------------------

export function getUserAccessToken(): string | null {
  return sessionStorage.getItem(USER_ACCESS_TOKEN_KEY);
}

export function setUserAccessToken(token: string): void {
  sessionStorage.setItem(USER_ACCESS_TOKEN_KEY, token);
}

export function clearUserAccessToken(): void {
  sessionStorage.removeItem(USER_ACCESS_TOKEN_KEY);
}

/**
 * Expiração do access token de USUÁRIO (em timestamp absoluto, ms).
 *
 * - Não aplica margem de segurança aqui; guardamos o valor "puro".
 * - A lógica de "quando considerar expirado" fica a cargo da camada de auth.
 */
export function setUserAccessTokenExpiresAt(expiresAt: number | null): void {
  if (!expiresAt) {
    sessionStorage.removeItem(USER_ACCESS_TOKEN_EXPIRES_AT_KEY);
    return;
  }

  sessionStorage.setItem(
    USER_ACCESS_TOKEN_EXPIRES_AT_KEY,
    String(expiresAt),
  );
}

export function getUserAccessTokenExpiresAt(): number | null {
  const raw = sessionStorage.getItem(USER_ACCESS_TOKEN_EXPIRES_AT_KEY);
  if (!raw) {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
}

// ---------------------------------------------------------------------------
// Tokens de APLICAÇÃO
// ---------------------------------------------------------------------------

export function getApplicationAccessToken(): string | null {
  return sessionStorage.getItem(APP_ACCESS_TOKEN_KEY);
}

export function setApplicationAccessToken(token: string): void {
  sessionStorage.setItem(APP_ACCESS_TOKEN_KEY, token);
}

export function clearApplicationAccessToken(): void {
  sessionStorage.removeItem(APP_ACCESS_TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// API LEGADA / GENÉRICA
// ---------------------------------------------------------------------------

/**
 * Mantido por compatibilidade.
 * Hoje é interpretado como "token de usuário".
 */
export function getAccessToken(): string | null {
  return getUserAccessToken();
}

/**
 * Mantido por compatibilidade.
 * Hoje escreve apenas o token de usuário.
 */
export function setAccessToken(token: string): void {
  setUserAccessToken(token);
}

export function clearAccessToken(): void {
  clearUserAccessToken();
}

/**
 * Obtém o token "corrente" para chamadas HTTP genéricas.
 * Prioridade: usuário > aplicação.
 */
export function getCurrentAccessToken(): string | null {
  return getUserAccessToken() ?? getApplicationAccessToken();
}

/**
 * Refresh token (planejado)
 *
 * Mantemos as funções para já padronizar o contrato.
 * Quando o refresh token for implementado, basta conectar aqui
 * e ativar o fluxo no `httpClient.ts`.
 */
export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function clearAllTokens(): void {
  clearUserAccessToken();
  clearApplicationAccessToken();
  clearRefreshToken();
  sessionStorage.removeItem(USER_ACCESS_TOKEN_EXPIRES_AT_KEY);
}

// ---------------------------------------------------------------------------
// Basic Auth centralizado para integrações OAuth
// ---------------------------------------------------------------------------

export interface BasicAuthCredentials {
  consumerKey: string;
  consumerSecret: string;
}

/**
 * Gera o header Authorization no formato Basic.
 *
 * Centralizado aqui para ser reutilizado por:
 * - clients OAuth de usuário e aplicação
 * - factories compartilhadas baseadas em httpClient
 *
 * Mantém o mesmo comportamento do helper legado em
 * `src-next/modules/auth/utils/basicAuth.ts`.
 */
export function createBasicAuthorizationHeader({}: BasicAuthCredentials): string {
  const encoded = base64.encode(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
  return `Basic ${encoded}`;
}
